// MACHINERY V2 (Stroje) — the drag-and-drop file well (M2.6).
//
// >>> SHARED V2 PRIMITIVE (the FormField kit). IMPORT IT — DO NOT REINVENT. <<<
//
// PROMOTED, NOT COPIED. This is M2.5's `UploadDropzone` from
// profile/tabs/docsUploadParts.tsx moved into the kit verbatim (same markup,
// same cloud glyph, same drag tint) with its two hard-wired i18n keys lifted
// into `hint` / `limit` props — because the SECOND consumer needs different
// copy, not a different control:
//   · Dokumenty upload dialog — "any file type · max 11 MB per file" (§6).
//   · SR create attachments   — "PDF, images, documents up to 10 MB" (§7).
// The two caps stay PER SURFACE and are deliberately NOT unified (ticket §5 #6:
// "Manuals cap 11MB vs SR-create 10MB (keep per-surface)") — this component
// never enforces a cap, it only renders the line its caller passes.
//
// Reference impl: design_handoff_machinery_v3/machinery-sr.jsx `DropZone`
// (lines 667-689); artboards 0702 / 0703 (SR create) and 1004 (Dokumenty).
//
// NO `accept` ATTRIBUTE, ON PURPOSE: the v1 dropzones on BOTH surfaces accept
// any file type (CreateServiceReportPage.tsx:898-904 — a bare
// `<input type="file" multiple>`), and artboard 1004 repeats it in its own
// footnote. The hint line is advisory copy, not a filter.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useState } from "react";

export interface FormDropzoneProps {
  /** Already-translated main line. */
  hint: string;
  /** Already-translated mono line under it (formats / size cap). */
  limit: string;
  onFiles: (files: File[]) => void;
  /** Paints the well red — the caller has an unresolved file error. */
  invalid?: boolean;
}

const CloudGlyph: React.FC = () => (
  <svg
    viewBox="0 0 24 18"
    width="30"
    height="22"
    fill="none"
    stroke="var(--muted)"
    strokeWidth="1.3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M5.5 11.5 a4 4 0 0 1 1.3 -7.8 c0.6 -2 2.5 -3.4 4.7 -3.4 c2.6 0 4.8 2 4.9 4.5 c1.6 0.5 2.6 1.9 2.6 3.7 c0 2.1 -1.6 3.7 -3.7 3.7 H6.5" />
    <line x1="12" y1="8" x2="12" y2="14" />
    <path d="M9.5 10.5 L12 8 L14.5 10.5" />
  </svg>
);

const FormDropzone: React.FC<FormDropzoneProps> = ({ hint, limit, onFiles, invalid }) => {
  const [dropping, setDropping] = useState(false);

  const border = dropping
    ? "var(--mod-accent)"
    : invalid
    ? "var(--red)"
    : "var(--border)";

  return (
    <label
      onDragOver={(e) => {
        e.preventDefault();
        setDropping(true);
      }}
      onDragLeave={() => setDropping(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDropping(false);
        onFiles(Array.from(e.dataTransfer.files || []));
      }}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 3,
        padding: "22px 14px 18px",
        background: dropping ? "var(--mod-accent-soft)" : "var(--bg-soft)",
        border: "1px dashed " + border,
        borderRadius: 8,
        cursor: "pointer",
        textAlign: "center",
      }}
    >
      <CloudGlyph />
      <span style={{ fontSize: 13, color: "var(--ink-2)" }}>{hint}</span>
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--faint)" }}>
        {limit}
      </span>
      <input
        type="file"
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          onFiles(Array.from(e.target.files || []));
          e.target.value = "";
        }}
      />
    </label>
  );
};

export default FormDropzone;
